import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Sidebar, Topbar } from './components/Layout';
import { Spin } from './components/UI';
import './index.css';

import Login           from './pages/Login';
import Dashboard       from './pages/Dashboard';
import Customers       from './pages/Customers';
import CustomerProfile from './pages/CustomerProfile';
import Enquiry         from './pages/Enquiry';
import Complaints      from './pages/Complaints';
import Service         from './pages/Service';
import Sales           from './pages/Sales';
import Inventory       from './pages/Inventory';
import Templates       from './pages/Templates';
import Staff           from './pages/Staff';
import Settings        from './pages/Settings';
import Expenses        from './pages/Expenses';
import Reports         from './pages/Reports';
import PaymentFUPs     from './pages/PaymentFUPs';
import Quotations      from './pages/Quotations';
import Assignments     from './pages/Assignments';
import Attendance      from './pages/Attendance';
import ChangePassword  from './pages/ChangePassword';
import AuditTrail             from './pages/AuditTrail';
import ServicePickups          from './pages/ServicePickups';
import FieldPortal             from './pages/FieldPortal';
import SalaryAcknowledgements  from './pages/SalaryAcknowledgements';
import DailyWagesSalary        from './pages/DailyWagesSalary';

function Shell({ greeting, sub, children }) {
  return (
    <div className="shell">
      <Sidebar/>
      <div className="main-col">
        <Topbar greeting={greeting} sub={sub}/>
        <div className="page-wrap">{children}</div>
      </div>
    </div>
  );
}

function Protected({ children, greeting, sub }) {
  const { user, ready } = useAuth();
  if (!ready) return <Spin label="Loading…"/>;
  if (!user)  return <Navigate to="/login" replace/>;
  return <Shell greeting={greeting} sub={sub}>{children}</Shell>;
}

function AdminProtected({ children, greeting, sub }) {
  const { user, ready, isAdmin } = useAuth();
  if (!ready)   return <Spin label="Loading…"/>;
  if (!user)    return <Navigate to="/login" replace/>;
  if (!isAdmin) return <Navigate to="/dashboard" replace/>;
  return <Shell greeting={greeting} sub={sub}>{children}</Shell>;
}

function SAProtected({ children, greeting, sub }) {
  const { user, ready, isSA } = useAuth();
  if (!ready) return <Spin label="Loading…"/>;
  if (!user)  return <Navigate to="/login" replace/>;
  if (!isSA)  return <Navigate to="/dashboard" replace/>;
  return <Shell greeting={greeting} sub={sub}>{children}</Shell>;
}

function FieldProtected({ children }) {
  const { user, ready, isField } = useAuth();
  if (!ready)   return <Spin label="Loading…"/>;
  if (!user)    return <Navigate to="/login" replace/>;
  if (!isField) return <Navigate to="/dashboard" replace/>;
  return <>{children}</>;
}

function DashboardWrapper() {
  const { user, isField } = useAuth();
  if (isField) return <Navigate to="/field-portal" replace/>;
  const h = new Date().getHours();
  const g = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return (
    <Protected greeting={`${g}, ${user?.name || 'Sakthi Solar'}`} sub="Your business snapshot for today">
      <Dashboard/>
    </Protected>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login"           element={<Login/>}/>
          <Route path="/change-password" element={<ChangePassword/>}/>
          <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
          <Route path="/dashboard"     element={<DashboardWrapper/>}/>
          <Route path="/customers"     element={<Protected greeting="Customers" sub="Manage registered customers"><Customers/></Protected>}/>
          <Route path="/customers/:id" element={<Protected greeting="Customer Profile" sub="Full history and actions"><CustomerProfile/></Protected>}/>
          <Route path="/enquiry"       element={<Protected greeting="Enquiry Management" sub="Track and respond to enquiries"><Enquiry/></Protected>}/>
          <Route path="/complaints"    element={<Protected greeting="Complaints" sub="Raise and track customer complaints"><Complaints/></Protected>}/>
          <Route path="/service"       element={<Protected greeting="Service Requests" sub="Schedule and track field service"><Service/></Protected>}/>
          <Route path="/sales"         element={<Protected greeting="Sales & Billing" sub="Record sales and generate invoices"><Sales/></Protected>}/>
          <Route path="/inventory"     element={<Protected greeting="Inventory" sub="Manage products and stock levels"><Inventory/></Protected>}/>
          <Route path="/templates"     element={<Protected greeting="Message Templates" sub="Manage enquiry and follow-up templates"><Templates/></Protected>}/>
          <Route path="/staff"         element={<AdminProtected greeting="Staff Management" sub="Manage team members and roles"><Staff/></AdminProtected>}/>
          <Route path="/settings"      element={<SAProtected greeting="Settings" sub="Company configuration"><Settings/></SAProtected>}/>
          <Route path="/expenses"       element={<AdminProtected greeting="Expenses" sub="Track operational expenses"><Expenses/></AdminProtected>}/>
          <Route path="/reports"        element={<AdminProtected greeting="Reports" sub="Income and expense reports"><Reports/></AdminProtected>}/>
          <Route path="/payment-fups"   element={<AdminProtected greeting="Payment Follow-Ups" sub="Track part payments and schedule follow-ups"><PaymentFUPs/></AdminProtected>}/>
          <Route path="/quotations"     element={<AdminProtected greeting="Quotations & Estimates" sub="Create and manage quotations for customers"><Quotations/></AdminProtected>}/>
          <Route path="/assignments"    element={<Protected greeting="Field Assignments" sub="Active complaints and service tasks assigned to field staff"><Assignments/></Protected>}/>
          <Route path="/attendance"     element={<AdminProtected greeting="Attendance" sub="Mark and track staff attendance"><Attendance/></AdminProtected>}/>
          <Route path="/audit-trail"      element={<AdminProtected greeting="Audit Trail" sub="System-wide action log — who did what and when"><AuditTrail/></AdminProtected>}/>
          <Route path="/service-pickups"  element={<Protected greeting="Service Pickups" sub="Track inverters taken from customers for service"><ServicePickups/></Protected>}/>
          <Route path="/field-portal"     element={<FieldProtected><FieldPortal/></FieldProtected>}/>
          <Route path="/salary-acks"      element={<SAProtected greeting="Salary Acknowledgements" sub="Staff salary acknowledgements — superadmin view"><SalaryAcknowledgements/></SAProtected>}/>
          <Route path="/daily-wages"      element={<SAProtected greeting="Daily Wages Salary" sub="Attendance-based salary calculator for daily wage staff"><DailyWagesSalary/></SAProtected>}/>
          <Route path="*"              element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
